// DiscoveryOrganizationHandler.js
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { useStytchB2BClient, useStytchMemberSession } from "@stytch/react/b2b";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLogo from "../assets/DashboardLogo";
import Flx from "../components/layout/Flx";

const DiscoveryOrganizationHandler = () => {
  const navigate = useNavigate();
  const stytchClient = useStytchB2BClient();
  const { session } = useStytchMemberSession();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [discoveredOrgs, setDiscoveredOrgs] = useState([]);
  const [creatingOrg, setCreatingOrg] = useState(false);

  useEffect(() => {
    checkDiscoverySession();
  }, []);

  const checkDiscoverySession = async () => {
    try {
      // Check if we have a discovery session
      const discoverySession = await stytchClient.discovery.getSession();

      if (!discoverySession) {
        throw new Error("No discovery session found");
      }

      // Get list of discovered organizations
      const orgsResponse = await stytchClient.discovery.organizations.list();

      if (
        orgsResponse.discovered_organizations &&
        orgsResponse.discovered_organizations.length > 0
      ) {
        setDiscoveredOrgs(orgsResponse.discovered_organizations);

        // Check if user should auto-join an organization
        const pendingOrgCreation = localStorage.getItem("pendingOrgCreation");
        if (
          !pendingOrgCreation &&
          orgsResponse.discovered_organizations.length === 1
        ) {
          // Auto-join if only one org is discovered
          await handleSelectOrganization(
            orgsResponse.discovered_organizations[0].organization
              .organization_id
          );
          return;
        }
      } else {
        // No organizations discovered, check if we need to create one
        const pendingOrgCreation = localStorage.getItem("pendingOrgCreation");
        if (pendingOrgCreation) {
          await handleCreateOrganization();
        }
      }

      setLoading(false);
    } catch (err) {
      console.error("Discovery session error:", err);
      setError(
        "Failed to retrieve discovery session. Please try logging in again."
      );
      setLoading(false);
    }
  };

  const handleCreateOrganization = async () => {
    const pendingOrgData = JSON.parse(
      localStorage.getItem("pendingOrgCreation") || "{}"
    );

    if (!pendingOrgData.organizationName) {
      setError("No organization data found");
      return;
    }

    setCreatingOrg(true);
    setError("");

    try {
      // Create organization via discovery
      const createResponse = await stytchClient.discovery.organizations.create({
        organization_name: pendingOrgData.organizationName,
        organization_slug: pendingOrgData.organizationSlug,
        session_duration_minutes: 10080, // 7 days
        sso_jit_provisioning: "NOT_ALLOWED",
        email_allowed_domains: [],
        email_jit_provisioning: "RESTRICTED",
        email_invites: "ALL_ALLOWED",
        auth_methods: "EMAIL_MAGIC_LINKS",
        allowed_auth_methods: ["EMAIL_MAGIC_LINKS", "SSO"],
      });

      if (createResponse.status_code === 200) {
        // Clear pending org creation data
        localStorage.removeItem("pendingOrgCreation");

        // Update user profile with stored data
        const pendingUserData = localStorage.getItem("pendingUserData");
        if (pendingUserData) {
          const userData = JSON.parse(pendingUserData);
          // You might want to make an API call here to update the member's name
          localStorage.removeItem("pendingUserData");
        }

        // Organization created and user is now logged in
        navigate("/");
      } else {
        throw new Error("Failed to create organization");
      }
    } catch (err) {
      console.error("Organization creation error:", err);
      setError("Failed to create organization. Please try again.");
      setCreatingOrg(false);
    }
  };

  const handleSelectOrganization = async (organizationId) => {
    setLoading(true);
    setError("");

    try {
      // Exchange discovery session for organization session
      const exchangeResponse = await stytchClient.discovery.exchangeSession({
        organization_id: organizationId,
        session_duration_minutes: 10080, // 7 days
      });

      if (exchangeResponse.status_code === 200) {
        // Clear any pending data
        localStorage.removeItem("pendingOrgCreation");

        // Update user profile if needed
        const pendingUserData = localStorage.getItem("pendingUserData");
        if (pendingUserData) {
          const userData = JSON.parse(pendingUserData);
          // You might want to make an API call here to update the member's name
          localStorage.removeItem("pendingUserData");
        }

        // Successfully joined organization
        navigate("/");
      } else {
        throw new Error("Failed to join organization");
      }
    } catch (err) {
      console.error("Organization selection error:", err);
      setError("Failed to join organization. Please try again.");
      setLoading(false);
    }
  };

  const handleRequestNewOrg = () => {
    // Show form to collect organization details
    const orgName = prompt("Enter your organization name:");
    if (orgName) {
      const orgSlug = orgName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      localStorage.setItem(
        "pendingOrgCreation",
        JSON.stringify({
          organizationName: orgName,
          organizationSlug: orgSlug,
          email: session?.user?.email || "",
        })
      );

      handleCreateOrganization();
    }
  };

  if (loading) {
    return (
      <Flx center column sx={{ minHeight: "100vh" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading organizations...</Typography>
      </Flx>
    );
  }

  return (
    <Flx
      center
      column
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 600,
          width: "100%",
          borderRadius: 2,
        }}
      >
        <Flx center column sx={{ mb: 4 }}>
          <DashboardLogo width="140px" sx={{ mb: 2 }} />
        </Flx>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {creatingOrg ? (
          <Flx center column>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>
              Creating your organization...
            </Typography>
          </Flx>
        ) : (
          <>
            {discoveredOrgs.length > 0 ? (
              <Box>
                <Typography variant="h5" gutterBottom align="center">
                  Select Your Organization
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }} align="center">
                  Choose an organization to join:
                </Typography>

                <List>
                  {discoveredOrgs.map((discoveredOrg) => (
                    <ListItem
                      key={discoveredOrg.organization.organization_id}
                      disablePadding
                    >
                      <ListItemButton
                        onClick={() =>
                          handleSelectOrganization(
                            discoveredOrg.organization.organization_id
                          )
                        }
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        <ListItemText
                          primary={discoveredOrg.organization.organization_name}
                          secondary={`Members: ${discoveredOrg.member_count || 0}`}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleRequestNewOrg}
                  sx={{ mt: 2 }}
                >
                  Create New Organization
                </Button>
              </Box>
            ) : (
              <Box>
                <Typography variant="h5" gutterBottom align="center">
                  Welcome!
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }} align="center">
                  No organizations found for your email domain.
                </Typography>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleRequestNewOrg}
                  disabled={creatingOrg}
                >
                  Create Your Organization
                </Button>

                <Typography
                  variant="body2"
                  sx={{ mt: 2 }}
                  align="center"
                  color="text.secondary"
                >
                  Or contact your administrator to be added to an existing
                  organization.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Flx>
  );
};

export default DiscoveryOrganizationHandler;
